// src/data/accessModel.js
//
// WHAT IS FREE, WHAT IS PAID — the single declarative statement of it.
//
// This file is the fleet template. Profound Philosophy is the first proper DDD
// app to ship a paywall, and the three sibling Profound apps (Living, Thinking,
// Questions) plus the wider fleet are expected to copy this shape. So: every
// product decision lives HERE as data, and nothing below the line in
// PremiumContext/PremiumGate needs editing to re-skin it for another app.
// A sibling app copies this file, changes the constants, and is done.
//
// THE LADDER (agreed 2026-09-13, mirrors Pemberton's):
//
//   Free              $0              The front door: enough to prove the app
//                                     is worth paying for.
//   <app> Forever     US$4.99 one-off All content. Bought in the App Store,
//                                     so buyers see their own storefront's
//                                     price (A$7.99 in Australia).
//   DDD subscription  from A$14.99/mo Everything above, plus the AI companion
//                                     beyond the free daily allowance.
//                                     Plus / Family / Family+.
//
// CLOUD SYNC IS NOT A PAID FEATURE (Nigel, 18 October 2026): any signed-in DDD
// account syncs, free plan included. It is part of having an account, not part
// of the ladder. (This file used to say sync was subscription-only; the code
// never gated it.)
//
// THE RULE THAT PROTECTS THE ENDGAME: the one-off purchase buys CONTENT, never
// the AI. The companion beyond her free allowance stays subscription-only, so a
// "forever" buyer still has a reason to subscribe once enough apps exist to
// make a monthly bundle attractive. Do not soften this when copying the file.
//
// AGREED 2026-09-15: no add-ons for v1. The one-off is already strong value, the
// problem was that nobody could see it. So the paywall now states what it adds,
// measured from the content (courseValue below), and appears at the moment the
// free course ends. Revisit add-ons with conversion data; Original Position
// scenario packs are the first candidate.
//
// Australian English throughout.

/** Entitlement id, matched by the RevenueCat entitlement and the webhook. */
export const FOREVER_ENTITLEMENT = 'philosophy_full';

/** App Store product id for the one-off purchase. */
export const FOREVER_PRODUCT_ID = 'com.divedeepdialogues.profoundphilosophy.forever';

/**
 * Modules free at the free tier, by `order` (see src/data/modules.js).
 *
 * 1 — What Is Philosophy?  the best hook in the app
 * 2 — Logic & Reasoning    the most immediately useful
 *
 * Two of eight, matching Pemberton's two of nine.
 */
export const FREE_MODULE_ORDERS = [1, 2];

/**
 * Tools free at the free tier.
 *
 * Deliberately derived from FREE_MODULE_ORDERS via the module→tool map rather
 * than listed by hand: a free module whose "Practise this now" card leads
 * straight into a locked tool is a broken promise, and hand-maintained lists
 * drift apart the first time a tool is renamed.
 */
export const freeToolIds = (moduleTools) => {
  const ids = new Set();
  for (const order of FREE_MODULE_ORDERS) {
    for (const tool of moduleTools[order] || []) ids.add(tool.id);
  }
  // The Original Position is always openable — its own modes carry the gate
  // (see GAME_MODE_ACCESS). A locked marquee feature cannot sell itself.
  ids.add('original-position');
  return ids;
};

/**
 * The Original Position's modes.
 *
 * Solo is the taste. Parlour (pass-and-play round one phone) and hosting a
 * linked table are the premium social experience — the reason the app is worth
 * $4.99 rather than nothing.
 *
 * JOINING a linked table stays free, always. The host has paid; taxing their
 * guests would shut down the one channel by which this app spreads. This is the
 * "host pass" shape already in DDD-PRICING-STRATEGY-2026.
 */
export const GAME_MODE_ACCESS = {
  'solo': 'free',
  'linked-join': 'free',
  'parlour': FOREVER_ENTITLEMENT,
  'linked-host': FOREVER_ENTITLEMENT,
};

/**
 * Whole surfaces that stay free.
 *
 * The assessment is the app's front door — it is the home page's hero call to
 * action ("Discover your unique philosophical profile in 5 minutes") and the
 * thing that shows a stranger the app is about them. Pemberton gated its
 * equivalent; that was the wrong call and we are not repeating it.
 *
 * Lineage and Journal were not in the original brief and are free by judgement:
 * the Lineage is a discovery surface that modules hand off into ("Meet Rawls"),
 * and the Journal holds the user's own writing — charging for access to your own
 * notes is hostile. Flagged for Nigel; easy to move.
 */
export const FREE_SURFACES = new Set([
  'assessment',
  'dashboard',
  'resources',
  'lineage',
  'journal',
  'positions',
  'privacy',
  'terms',
]);

/**
 * Subscription-only features. Never unlocked by the one-off purchase.
 * Cloud sync is deliberately absent: it comes with any signed-in account.
 *
 * NOTE ON THE COMPANION: Sofia is NOT gated in the client. The Knowledge Hub
 * enforces her allowance server-side (functions/coaching/entitlements.js:
 * free 5/day → add-on 25/day → Plus 75/day global), and CompanionPage already
 * renders the cap and the upgrade prompt from what the rail returns. Listing
 * her here documents the commercial model; it does not ask the client to block
 * her, and the client must not start.
 */
export const SUBSCRIPTION_FEATURES = new Set([
  'companion_beyond_free_tier',
]);

/**
 * Hub plans that are NOT paid, despite the names.
 *
 * The hub only writes the subscription claim while a plan is active, so the
 * safe test is "has a plan that isn't one of these" rather than an allowlist of
 * paid plan names — Pemberton shipped a hard-coded allowlist that missed the
 * hub's real 'plus' and 'family-plus' and silently denied paying subscribers.
 * 'explorer' is free despite sounding premium.
 */
export const FREE_PLANS = ['free', 'introduction', 'explorer', 'trial'];

/** Subscription states that mean "not currently paying". */
export const INACTIVE_STATUSES = ['cancelled', 'canceled', 'expired', 'suspended', 'past_due'];

/**
 * What the one-off purchase adds, measured from the content rather than typed
 * in, so the paywall can never quote a stale number after a module grows.
 *
 * @param modules      flat module list, each with `order` and a "52 min"-style `duration`
 * @param moduleTools  the module → tools map (src/data/moduleTools.js)
 */
export const courseValue = (modules, moduleTools) => {
  const minutes = (m) => parseInt(String(m?.duration ?? '').replace(/[^0-9]/g, ''), 10) || 0;
  const free = modules.filter((m) => FREE_MODULE_ORDERS.includes(m.order));
  const paid = modules.filter((m) => !FREE_MODULE_ORDERS.includes(m.order));
  const allTools = new Set(Object.values(moduleTools).flat().map((t) => t.id));
  const open = freeToolIds(moduleTools); // includes the Original Position, gated by mode
  return {
    freeMinutes: free.reduce((n, m) => n + minutes(m), 0),
    paidMinutes: paid.reduce((n, m) => n + minutes(m), 0),
    paidModuleCount: paid.length,
    paidToolCount: [...allTools].filter((id) => !open.has(id)).length,
  };
};

/** Minutes to a friendly figure, to the nearest half hour: 450 → "7½ hours". */
export const formatHours = (minutes) => {
  const halves = Math.round(minutes / 30);
  if (halves <= 1) return halves === 1 ? 'half an hour' : 'under half an hour';
  if (halves === 2) return '1 hour';
  const whole = Math.floor(halves / 2);
  return `${whole}${halves % 2 ? '½' : ''} hours`;
};
